import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an initial load has completed, with a minimum display time
 * for the skeleton so fast loads don't flash. Only applies to the FIRST load
 * per open-session — subsequent refetches (pagination, search) read raw state.
 *
 * Usage:
 *   const [hasLoadedOnce, markLoaded, reset] = useInitialLoadFlag(1000);
 *   // in fetch.finally: markLoaded()
 *   // on modal close: reset()
 */
export default function useInitialLoadFlag(minMs: number = 2000) {
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const openedAt = useRef<number>(Date.now());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const marked = useRef(false);

  const markLoaded = useCallback(() => {
    if (marked.current) return;
    marked.current = true;
    const elapsed = Date.now() - openedAt.current;
    const remaining = Math.max(0, minMs - elapsed);
    timer.current = setTimeout(() => setHasLoadedOnce(true), remaining);
  }, [minMs]);

  const reset = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    marked.current = false;
    openedAt.current = Date.now();
    setHasLoadedOnce(false);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  return [hasLoadedOnce, markLoaded, reset] as const;
}
